import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WarningCircle, Warning, Gavel, CurrencyGbp, ClockCounterClockwise, StackSimple, CaretRight } from '@phosphor-icons/react';
import { fetchDiaryIntelligence, type DiaryScope, type DiaryWarning, type DiaryWarningKind } from '@/lib/operationsApi';

/**
 * Diary Intelligence panel — presentation of the canonical /operations/intelligence signals for a
 * scope. Quiet when there is nothing to flag. Consumed by the barrister dashboard (scope=mine) and
 * reusable elsewhere. No detection logic here — that lives in the backend detector.
 *
 * De-prioritised: collapsed to a single summary line by default (matching the Tasks panel on the
 * barrister dashboard). The barrister expands it on click to see each warning. Keeps the dashboard
 * quiet — the day's detail lives in the fuller sections below, this is just the at-a-glance count.
 */
export function DiaryWarningsPanel({ scope, basePath, from, to, title = 'Heads up' }: {
  scope: DiaryScope;
  basePath: string;
  from?: string;
  to?: string;
  title?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data = [] } = useQuery({
    queryKey: ['diary-intel', scope, from, to],
    queryFn: () => fetchDiaryIntelligence({ scope, from, to }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const shown = [...data].sort((a, b) => sev(b.severity) - sev(a.severity)).slice(0, 6);
  if (shown.length === 0) return null;

  const actCount = shown.filter((w) => w.severity === 'act').length;
  const summary = actCount > 0
    ? `${actCount} to act${shown.length > actCount ? ` · ${shown.length - actCount} to watch` : ''}`
    : `${shown.length} to watch`;

  const go = (w: DiaryWarning) => {
    if (w.subjectType === 'hearing') navigate(`${basePath}/hearings/${w.subjectId}`);
    else if (w.subjectType === 'matter') navigate(`${basePath}/matters/${w.subjectId}`);
  };

  return (
    <div className="rounded-xl border border-amber-200/70 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left transition hover:bg-amber-50/70 dark:bg-amber-500/10"
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          <CaretRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} weight="bold" />
          <WarningCircle className="h-4 w-4" weight="fill" /> {title}
        </div>
        <span className={`text-xs font-medium ${actCount > 0 ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
          {summary}
        </span>
      </button>

      {open && (
        <div className="space-y-1 border-t border-amber-200/60 dark:border-amber-500/30 p-2">
          {shown.map((w, i) => (
            <button
              key={i}
              onClick={() => go(w)}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition hover:bg-muted/60 ${
                w.severity === 'act' ? 'text-red-700 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'
              }`}
            >
              <span className="shrink-0">{iconFor(w.kind)}</span>
              <span className="flex-1 truncate">{w.detail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const sev = (s: DiaryWarning['severity']) => (s === 'act' ? 2 : s === 'watch' ? 1 : 0);

function iconFor(kind: DiaryWarningKind) {
  const cls = 'h-4 w-4';
  switch (kind) {
    case 'double_book': return <WarningCircle className={cls} weight="fill" />;
    case 'missing_prep': return <Gavel className={cls} />;
    case 'blocked_obligation': return <StackSimple className={cls} />;
    case 'overdue_fee': return <CurrencyGbp className={cls} />;
    case 'dormant_matter': return <ClockCounterClockwise className={cls} />;
    default: return <Warning className={cls} weight="fill" />;
  }
}
